import { Memoize } from "@repo/helpers";
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import TextAreaMarkup from "./markup";
import { Props } from "./types";

/**
 * Claude did a lot of work on this component to get enter working. This should have just worked like a vanilla
 * textarea, but in the meantime, till we figure that out, this is very vibe coded
 *
 * @param props
 * @constructor
 */
export function TextArea(props: Props) {
	const cursor = useRef<number>();

	const [value, setValue] = useState(props.value);
	//In state rather than a ref so the effects below rebind as the element comes and goes
	const [element, setElement] = useState<HTMLTextAreaElement>(null);

	useEffect(() => {
		setValue(props.value);
	}, [props.value]);

	/**
	 * Puts the caret back where it was, since re-writing a controlled value sends it to
	 * the end of the text. Only safe to do once the textarea agrees with our value and
	 * the caret has actually moved, otherwise this undoes an edit the browser just made
	 * - like the newline from an Enter keypress.
	 */
	useEffect(() => {
		if (!element || cursor.current == null) return;
		if (element.value !== (value ?? "")) return;
		if (element.selectionStart === cursor.current && element.selectionEnd === cursor.current) return;
		element.selectionStart = cursor.current;
		element.selectionEnd = cursor.current;
	}, [element, value]);

	const updateValue = useCallback(
		function (value: any) {
			setValue(value);
			props.changeHandler(value, props.id);
		},
		[setValue, props.changeHandler, props.id],
	);

	/**
	 * On change handler. Calls valid function if provided and then calls the change
	 * handler
	 * @param event
	 */
	const onChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>) => {
			updateCursor(event.target);
			updateValue(event.target.value);
		},
		[updateValue],
	);

	const onChangeValue = useCallback(
		(value: any) => {
			if (value !== props.value) updateValue(value);
			//We want the masking thing to handle it's own cursor position
			//updateCursor(_inputEl.current);
		},
		[updateValue],
	);

	/**
	 * Insert newlines ourselves rather than leaning on the browser's default action for
	 * Enter. Pages hosting these apps cancel the keystroke to stop stray form submits,
	 * which also stops a textarea from ever getting a newline. Bound natively on the
	 * element, since React's own listener sits further up the tree and can be cut off
	 * before it ever runs.
	 */
	useEffect(() => {
		if (!element) return;
		element.addEventListener("keydown", onEnter);
		return () => element.removeEventListener("keydown", onEnter);

		function onEnter(event: globalThis.KeyboardEvent) {
			if (event.key !== "Enter" || event.ctrlKey || event.metaKey || event.altKey) return;
			if (event.isComposing || element.readOnly || element.disabled) return;

			event.preventDefault();
			if (insertedByBrowser()) return;

			const start = element.selectionStart ?? element.value.length;
			const end = element.selectionEnd ?? start;
			cursor.current = start + 1;
			updateValue(element.value.slice(0, start) + "\n" + element.value.slice(end));
		}

		/** Preferred route - it keeps the native undo stack and fires the usual change event. */
		function insertedByBrowser() {
			const before = element.value;
			try {
				return document.execCommand("insertText", false, "\n") && element.value !== before;
			} catch {
				return false;
			}
		}
	}, [element, updateValue]);

	/**
	 * A textarea owns the Enter key, so keep app level Enter shortcuts (submit, apply
	 * filters, enter-acts-like-tab) from acting on the keystroke as well. Native
	 * propagation is stopped too - React listens on the root container, so document
	 * level handlers would still see the event otherwise.
	 */
	const onKeyDown = useCallback(
		(event?: KeyboardEvent<HTMLTextAreaElement>) => {
			props.onKeyDown?.(event);
			if (event?.key !== "Enter" || event.ctrlKey || event.metaKey || event.altKey) return;
			event.stopPropagation();
			event.nativeEvent.stopPropagation();
		},
		[props.onKeyDown],
	);

	function updateCursor(element: HTMLTextAreaElement) {
		cursor.current = element.selectionStart;
	}

	return (
		<TextAreaMarkup
			{...props}
			onChange={onChange}
			onKeyDown={onKeyDown}
			inputRef={setElement}
			onChangeValue={onChangeValue}
			value={value}
		/>
	);
}

export default Memoize(TextArea);
